import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { AdminVehicle, OrganizationFleet } from '@/lib/types';

/**
 * Units as the console sees them. `/v1/vehicles` is scoped to the caller's own
 * organisation, which a SUPER_ADMIN onboarding a customer does not belong to,
 * so these go through `/v1/admin`.
 */

export interface VehicleInput {
  name: string;
  plateNumber: string;
  ident?: string | null;
  providerDeviceId?: string | null;
  provider?: string | null;
  brand?: string | null;
  model?: string | null;
  year?: number | null;
  color?: string | null;
  vin?: string | null;
  assetClass?: 'POWERED' | 'UNPOWERED';
  fuelTankVolume?: number | null;
  initialMileage?: number | null;
  speedLimit?: number | null;
  groupId?: string | null;
  driverId?: string | null;
}

export function useOrganizationFleet(organizationId: string) {
  return useQuery({
    queryKey: ['organizations', organizationId, 'vehicles'],
    queryFn: () =>
      apiFetch<OrganizationFleet>(`/v1/admin/organizations/${organizationId}/vehicles`),
    enabled: Boolean(organizationId),
  });
}

function useFleetInvalidation(organizationId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'vehicles'] });
    queryClient.invalidateQueries({ queryKey: ['partners'] });
  };
}

export function useCreateVehicle(organizationId: string) {
  const invalidate = useFleetInvalidation(organizationId);

  return useMutation({
    mutationFn: (data: VehicleInput) =>
      apiFetch<AdminVehicle>(`/v1/admin/organizations/${organizationId}/vehicles`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateVehicle(organizationId: string) {
  const invalidate = useFleetInvalidation(organizationId);

  return useMutation({
    mutationFn: ({ id, ...data }: VehicleInput & { id: string }) =>
      apiFetch<AdminVehicle>(`/v1/admin/vehicles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: invalidate,
  });
}
